// Pure helpers for the "request appointment" form — no I/O, no React.

/** Local yyyy-MM-dd for `now` — the `min` for the booking date field so past days can't be picked. */
export function todayDateInput(now: Date = new Date()): string {
  const m = (now.getMonth() + 1).toString().padStart(2, '0');
  const d = now.getDate().toString().padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

/** Combines a `yyyy-MM-dd` date and `HH:mm` time (interpreted as local wall-clock) into an ISO instant. */
export function combineDateTimeToIso(date: string, time: string): string | null {
  if (!date || !time) return null;
  const combined = new Date(`${date}T${time}`);
  if (Number.isNaN(combined.getTime())) return null;
  return combined.toISOString();
}

/**
 * True when the chosen local date + time is before `now` — the client-side pre-check that mirrors the
 * server's not-in-the-past guard. Empty/invalid entries are treated as not-past (nothing to validate yet).
 */
export function isBookingInPast(date: string, time: string, now: Date = new Date()): boolean {
  if (!date || !time) return false;
  const start = new Date(`${date}T${time}`);
  if (Number.isNaN(start.getTime())) return false;
  return start.getTime() < now.getTime();
}

/**
 * Latest selectable date for a reschedule request, as a `DateField` `max` — 30 days out from
 * `now`. Client-side v1 cap so patients can't drift years into the future by mistake.
 */
export function maxBookableDateInput(now: Date = new Date()): string {
  const max = new Date(now);
  max.setDate(max.getDate() + 30);
  return todayDateInput(max);
}

/**
 * Earliest selectable time-of-day for the reschedule time field, as a `TimeField` `minTime` —
 * the exact current wall-clock time when `date` is today (so past slots show disabled rather
 * than only failing on submit), or `undefined` for any other day (no floor).
 */
export function minSelectableTimeInput(date: string, now: Date = new Date()): string | undefined {
  if (!date || date !== todayDateInput(now)) return undefined;
  const h = now.getHours().toString().padStart(2, '0');
  const m = now.getMinutes().toString().padStart(2, '0');
  return `${h}:${m}`;
}
